import AbstractTrigger from '@/ghost/service/Triggers/AbstractTrigger'
import Events from '@/configuration/Events'
import WebSocketManager from '@/ghost/service/Requests/WebSocketManager'

export default class CreatePaymentFlowTrigger extends AbstractTrigger {
  constructor($locator) {
    super($locator, 'createPaymentFlow')
    this.browserRequests = $locator.browserRequests
  }

  async onEvent() {
    const method = this.$store.state.smartForm.activeMethod
    try {
      await super.onEvent({})

      // Check retry support for paymentFlow payment methods
      if (this.$store.getters.shouldPreventRetry(method)) {
        throw {
          errorCode: 'CLIENT_107',
          paymentMethod: method
        }
      }

      const { response } = await this.callProcessPayment(method)

      if (response.status === 'ERROR') {
        this.onError(response.answer, method)
      } else if (
        response.answer._type === 'V4/Payment' &&
        response.answer.orderStatus === 'UNPAID'
      ) {
        const paymentMethod = response.answer.transactions[0].paymentMethodType
        const errors = {
          BIZUM: 'ACQ_002'
        }

        const error = errors[paymentMethod]

        if (!error) throw { metadata: response.answer }

        this.onError(error, method)
      } else if (response.answer._type === 'V4/Charge/PaymentFlowResponse') {
        const data = { ...response.answer, method }

        this._setupExpiry(data)
        this._setupWebSocket(data)

        const { testBaseUrl, testCases, topLines, bottomLines } = data
        this.$store.dispatch('setNestedMethodTestData', {
          nestedMethod: method,
          testBaseUrl,
          testCases
        })
        this.$store.dispatch('setNestedMethodLabels', {
          method,
          labels: {
            topLines,
            bottomLines
          }
        })
      }
    } catch (error) {
      this.onError(error, method)
    }
  }

  async onError(
    error,
    method,
    path = 'ghost/service/CreatePaymentFlowTrigger.onEvent'
  ) {
    // If the method that is throwing the error is not open we don't want to display anything
    // Except for the "payment refused" case (ACQ_001)
    if (
      this.$store.getters.activeNestedMethod?.name === method ||
      error?.errorCode === 'ACQ_001'
    ) {
      super.onError(error, path)
    }
    // Always reset component status
    this.proxy.send(this.storeFactory.create('paymentFlowError', { method }))
  }

  async callProcessPayment(method) {
    const { formToken, publicKey, language, remoteId } = this.$store.state
    const {
      partialPaymentsAllowed,
      getNestedMethodExtraFields,
      getPaymentMethodCloseButtonType
    } = this.$store.getters
    const url = this.restAPI.addJSessionID(
      this.restAPI.getProcessPaymentUrl(this.endpoint)
    )

    const requestData = this.storeFactory.create('requestData', {
      url,
      objectData: {
        language,
        formToken,
        publicKey,
        paymentForm: {
          method,
          createPaymentFlow: true,
          ...getNestedMethodExtraFields(method)
        },
        remoteId,
        clientVersion: this.restAPI.getClientVersion(),
        device: this.restAPI.getDeviceData(),
        wsUser: this.restAPI.getWSUser(),
        paymentMethodCloseButton: getPaymentMethodCloseButtonType,
        partialPaymentsAllowed,
        displayMode: 'MINIMAL',
        refererUrl: window.document.referrer
      },
      headers: {},
      options: {}
    })
    return await this.browserRequests.post(requestData)
  }

  _setupExpiry(data) {
    if (data.expirationDate) {
      const { expirationDate, method } = data

      // TO REMOVE IN THE FUTURE!
      // As the server is not ready yet for the implementation of the dynamic secure additional delay, we will add a fixed time.
      // 30 sec - Only for QR methods
      // See KJS-6183
      let { secureAdditionalDelay } = data
      if (!secureAdditionalDelay && data.flow === 'QR_CODE')
        secureAdditionalDelay = 30

      let { additionalDelay, expiration } = this._getMockExpiryValues(data)

      // If values are not mocked use the ones provided in the process payment call
      if (!additionalDelay) additionalDelay = secureAdditionalDelay ?? 0
      if (!expiration) expiration = expirationDate

      // Base expiry object without the secureDelay
      const expiryObj = {
        method,
        warnExpiry: null,
        expiry: expiration
      }

      // Setup secure additional delay
      if (additionalDelay > 0) {
        // The warning state will be the original expiration date
        expiryObj.warnExpiry = expiration

        // The new expiration date will be the provided expiry + the provided secureAditionalDelay in seconds
        const extendedExpiryDate = new Date(expiration)
        extendedExpiryDate.setTime(
          extendedExpiryDate.getTime() + additionalDelay * 1000
        )
        const extendedExpiry = extendedExpiryDate.toISOString()
        expiryObj.expiry = extendedExpiry
      }

      // Used to setUp the expiry on the webSocket
      data.finalExpiryDate = expiryObj.expiry
      this.$store.dispatch('setMethodExpiry', expiryObj)
    }
  }

  _getMockExpiryValues(data) {
    const mockedValues = {
      additionalDelay: null,
      expiration: null
    }

    // Check for mocked values in _internals
    const nestedMethodMockConfig =
      this.$store.state._internals.nestedMethods[data.method]
    if (nestedMethodMockConfig) {
      const { forcedSecureAdditionalDelay, forcedExpiryTimer } =
        nestedMethodMockConfig

      if (forcedSecureAdditionalDelay)
        mockedValues.additionalDelay = forcedSecureAdditionalDelay

      if (forcedExpiryTimer) {
        // Timer specified in seconds, get expiry date based on curDate + provided seconds
        const forcedExpiryDate = new Date()
        forcedExpiryDate.setTime(
          forcedExpiryDate.getTime() + forcedExpiryTimer * 1000
        )
        mockedValues.expiration = forcedExpiryDate.toISOString()
      }
    }

    return mockedValues
  }

  _setupWebSocket(data) {
    if (this.$store.state.smartForm.activeMethod !== data.method) return

    const socket = new WebSocketManager(data.websocketUrl)

    if (data.finalExpiryDate) {
      // Get expiry date difference with current time in seconds
      const expiry = new Date(data.finalExpiryDate).getTime() - Date.now()

      // Close the connection when timer expires
      setTimeout(() => {
        // If socket didn't manage to open (WebSocket connection to URL failed for example)
        // Throw a different error as the close event from the websocket will not be triggered
        if (!socket.open) this.onError('CLIENT_606', data.method)

        socket.close()
      }, expiry)
    }

    // If method is closed close socket connection from our side
    this.$bus.$once(Events.krypton.message.nestedMethodReset, msg => {
      if (msg.method === data.method) {
        // Manually closing the socket for back button click use case
        socket.close(false)
      }
    })

    // When we receive the open event we are ready to display the component
    socket.on('open', _event => {
      this.proxy.send(this.storeFactory.create('paymentFlowCreated', data))
    })

    // Expected close event
    socket.on('close', async _event => {
      // Check transaction and try declining it on close
      await this.wsCallWrapper(this.declineTransaction(data.uuid), data.method)
    })

    socket.on('message', async event => {
      await this._messageListener(event, data.method)
    })
  }

  async _messageListener(event, method) {
    // event contains a JSON string with the properties type and state

    const { type, state, data } = JSON.parse(event)
    // Return if state is cancelled, this is triggered when calling the DeclineTransaction WS
    // In that case we don't want to call getTransaction again
    if (type === 'FINALIZE' && state === 'CANCELLED') return
    // Display secure additional delay (KJS-6244)
    if (
      type === 'NOTIFY' &&
      data?.action === 'DISPLAY_SECURE_ADDITIONAL_DELAY'
    ) {
      await this.proxy.send(
        this.storeFactory.create('paymentFlowDisplayAdditionalDelay', {
          method
        })
      )
      return
    }

    await this.wsCallWrapper(this.getTransaction(), method)
  }

  getTransaction() {
    return async method => {
      // Default flow, called when we receive a message from the websocket
      // make a getTransaction call and manage the error/redirection according to the received transaction
      const { transactionTrigger } = this.$locator
      const transactionStore = this.getTransactionStore(method)
      const res = await transactionTrigger.callGetTransaction(transactionStore)
      transactionTrigger.manageResponse(res, transactionStore)
    }
  }

  // Called when connection is closed
  declineTransaction(transactionUuid) {
    return async method => {
      const { declinePaymentTrigger } = this.$locator

      // This ws tries to decline the transaction or returns the accepted transaction if possible
      const res = await declinePaymentTrigger.callDeclinePayment(
        transactionUuid
      )

      if (res.answer.clientAnswer.orderCycle === 'CLOSED') {
        // if orderCycle is closed continue the flow into the default transactionTrigger
        // This will error or redirect depending on the transaction
        const { transactionTrigger } = this.$locator
        const transactionStore = this.getTransactionStore(method)
        transactionTrigger.manageResponse(res, transactionStore)
      } else {
        // if orderCycle is open trigger the generic "expired" error message
        this.onError('CLIENT_605', method)
      }
    }
  }

  async wsCallWrapper(wsCall, method) {
    // Mark payment method as attempted
    this.$store.dispatch('addPaymentMethodAttempt', method)

    try {
      await wsCall(method)
    } catch (error) {
      if (!error.errorCode) error.errorCode = 'ACQ_001'
      this.onError(error, method)
    }
  }

  getTransactionStore(method) {
    const { formToken, publicKey, dna, remoteId } = this.$store.state

    return this.storeFactory.create('getTransaction', {
      paymentMethod: method,
      formToken,
      publicKey,
      remoteId,
      dna,
      // Running status is accepted as long as orderCycle is closed
      // OPEN orderCycle is accepted as long as orderStatus is PARTIALLY_PAID
      successfulResponses: [
        { orderCycle: 'CLOSED', 'transactions.0.status': 'PAID' },
        { orderCycle: 'CLOSED', 'transactions.0.status': 'RUNNING' },
        { orderCycle: 'OPEN', orderStatus: 'PARTIALLY_PAID' }
      ]
    })
  }
}
